import React from 'react';
import { Database, FolderTree, MessageSquare, Brain, Coins, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFolderStore } from '../store/folderStore';
import { useChatStore } from '../store/chatStore';
import { apiService } from '../services/api';
import { Folder } from '../types';
import config from '../config';
import { useAuthStore } from '../store/authStore';

type SettingsSection = 'statistics' | 'memory' | 'profile';

const sections: Array<{ id: SettingsSection; label: string; description: string }> = [
  {
    id: 'statistics',
    label: 'Statistics/Usage',
    description: 'Overview of your workspace usage and activity.',
  },
  {
    id: 'memory',
    label: 'Memory',
    description: 'Memory usage and retention policies.',
  },
  {
    id: 'profile',
    label: 'Profile',
    description: 'Manage your account profile and preferences.',
  },
];

const countFolders = (folders: Folder[] = []): number => {
  return folders.reduce<number>((acc, folder) => {
    const childrenCount = folder.children ? countFolders(folder.children) : 0;
    return acc + 1 + childrenCount;
  }, 0);
};

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeSection, setActiveSection] = React.useState<SettingsSection>('statistics');
  const [usageData, setUsageData] = React.useState<{
    token_usage_total: number;
    token_usage_limit: number | null;
  } | null>(null);
  const [isLoadingUsage, setIsLoadingUsage] = React.useState(false);

  const {
    folders,
    isLoading: foldersLoading,
    fetchFolders,
  } = useFolderStore(React.useCallback((state) => ({
    folders: state.folders,
    isLoading: state.isLoading,
    fetchFolders: state.fetchFolders,
  }), []));

  const {
    threads,
    isLoadingThreads,
    fetchThreads,
  } = useChatStore(React.useCallback((state) => ({
    threads: state.threads,
    isLoadingThreads: state.isLoadingThreads,
    fetchThreads: state.fetchThreads,
  }), []));

  React.useEffect(() => {
    if (!folders.length && !foldersLoading) {
      fetchFolders().catch((error) => {
        console.error('Failed to fetch folders for settings:', error);
      });
    }
  }, [folders.length, foldersLoading, fetchFolders]);

  React.useEffect(() => {
    if (!threads.length && !isLoadingThreads) {
      fetchThreads().catch((error) => {
        console.error('Failed to fetch threads for settings:', error);
      });
    }
  }, [threads.length, isLoadingThreads, fetchThreads]);

  // Fetch usage data
  React.useEffect(() => {
    const fetchUsage = async () => {
      setIsLoadingUsage(true);
      try {
        const usage = await apiService.getUsageSummary();
        setUsageData({
          token_usage_total: usage.token_usage_total || 0,
          token_usage_limit: usage.token_usage_limit,
        });
      } catch (error: any) {
        console.error('Failed to fetch usage data:', error);
        // Handle 404 (no subscription) gracefully
        if (error.response?.status === 404) {
          // Set default values for users without subscription
          setUsageData({
            token_usage_total: 0,
            token_usage_limit: null, // Unlimited or not available
          });
        } else {
          // For other errors, set to null to show error message
          setUsageData(null);
        }
      } finally {
        setIsLoadingUsage(false);
      }
    };

    if (activeSection === 'statistics') {
      fetchUsage();
    }
  }, [activeSection]);

  const totalFolders = React.useMemo(() => countFolders(folders), [folders]);
  const totalThreads = React.useMemo(() => threads.length, [threads]);

  const renderSection = () => {
    if (activeSection === 'statistics') {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Workspace Statistics</h2>
            <p className="mt-2 text-sm text-gray-600">
              Track how you are using {config.appName} across folders and conversations.
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6 sm:grid-cols-2">
            <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
              <div className="flex items-center space-x-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <FolderTree className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Folders</p>
                  <p className="mt-1 text-3xl font-bold text-gray-900">
                    {foldersLoading ? '—' : totalFolders}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
              <div className="flex items-center space-x-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <MessageSquare className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Threads</p>
                  <p className="mt-1 text-3xl font-bold text-gray-900">
                    {isLoadingThreads ? '—' : totalThreads}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100 sm:col-span-2">
              <div className="flex items-center space-x-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <Coins className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">Credits Usage (This Month)</p>
                  {isLoadingUsage ? (
                    <p className="mt-1 text-3xl font-bold text-gray-900">—</p>
                  ) : usageData !== null ? (
                    <div className="mt-2">
                      <div className="flex items-baseline space-x-2">
                        <p className="text-3xl font-bold text-gray-900">
                          {usageData.token_usage_total.toLocaleString()}
                        </p>
                        {usageData.token_usage_limit !== null && usageData.token_usage_limit > 0 && (
                          <p className="text-lg text-gray-500">
                            / {usageData.token_usage_limit.toLocaleString()}
                          </p>
                        )}
                      </div>
                      {usageData.token_usage_limit !== null && usageData.token_usage_limit > 0 ? (
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary-600 h-2 rounded-full transition-all"
                              style={{
                                width: `${Math.min((usageData.token_usage_total / usageData.token_usage_limit) * 100, 100)}%`
                              }}
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {Math.round((usageData.token_usage_total / usageData.token_usage_limit) * 100)}% used
                          </p>
                        </div>
                      ) : (
                        <p className="mt-1 text-sm text-gray-500">
                          {usageData.token_usage_limit === null ? 'Unlimited' : `${usageData.token_usage_total.toLocaleString()} credits used`}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-gray-500">Unable to load usage data</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeSection === 'memory') {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Memory Usage</h2>
            <p className="mt-2 text-sm text-gray-600">
              Review how much contextual memory the assistant is storing.
            </p>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <div className="flex items-center space-x-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                <Brain className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Memory</p>
                <p className="mt-1 text-3xl font-bold text-gray-900">
                  Coming soon
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              Detailed memory insights will appear here as we expand the retention features.
            </p>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Stay tuned</h3>
            <p className="mt-2 text-sm text-gray-600">
              We are working on advanced tools to let you inspect and manage conversation memory at a granular level.
            </p>
          </div>
        </div>
      );
    }

    if (activeSection === 'profile') {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Profile</h2>
            <p className="mt-2 text-sm text-gray-600">
              Review and update your account details.
            </p>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <div className="flex items-center space-x-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                <User className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Signed in as</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {user?.display_name || user?.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-sm text-gray-500">{user?.email || ''}</p>
              </div>
            </div>

            <div className="mt-5">
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
              >
                Open Profile
              </button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-sm text-gray-600">
            Configure your {config.appName} experience and explore analytics.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-12">
          <nav className="md:col-span-4 lg:col-span-3 order-2 md:order-1">
            <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
              <h2 className="px-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Preferences
              </h2>
              <div className="mt-3 space-y-1">
                {sections.map((section) => {
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full rounded-xl px-3 py-2 text-left transition ${
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <p className="text-sm font-medium">{section.label}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {section.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          </nav>

          <section className="md:col-span-8 lg:col-span-9 order-1 md:order-2">
            <div className="space-y-6">
              <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                    <Database className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {sections.find((section) => section.id === activeSection)?.label}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">
                      {sections.find((section) => section.id === activeSection)?.description}
                    </p>
                  </div>
                </div>
              </div>

              {renderSection()}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Settings;

